'use client';

import {
  HostSlotLoaderProvider,
  type HostSlotLoader,
} from '@highereducation/dynamic-experience';
import {
  createHostSlotLoader,
  type Integrations,
  type RegistrySnapshot,
} from '@highereducation/mfe-loader';
import { useMemo } from 'react';
import { createOneTrustConsent } from '@hen-site/lib/microFrontends/oneTrustConsent';
import { useMicroFrontendRegistry } from './MicroFrontendRegistryProvider';

/**
 * Fills `platform.hostSlot` placements from the tenant's registry.
 *
 * The registry is read by tenant slug and the flow's owning tenant is the
 * flow tenant id; the loader holds both and the two must not be mixed.
 * Consent is read from OneTrust's standard group ids.
 */
export function MicroFrontendLoaderProvider({
  tenantSlug,
  flowTenantId,
  integrations,
  children,
}: {
  tenantSlug: string;
  flowTenantId: string;
  /** Integrations by registry `integration` kind. */
  integrations?: Integrations;
  children: React.ReactNode;
}): React.ReactElement {
  const registry = useMicroFrontendRegistry();
  const consent = useMemo(() => createOneTrustConsent(), []);

  const loader: HostSlotLoader = useMemo(
    () =>
      createHostSlotLoader({
        // This app compiles without strictNullChecks, which makes the parsed
        // registry's required fields look optional. The loader's own strict
        // build holds the real shape.
        registry: registry as RegistrySnapshot,
        tenantSlug,
        flowTenantId,
        consent,
        integrations,
      }),
    [registry, tenantSlug, flowTenantId, consent, integrations],
  );

  return (
    <HostSlotLoaderProvider value={loader}>{children}</HostSlotLoaderProvider>
  );
}
